/* eslint-disable no-unsafe-optional-chaining */
import React, { useState, useEffect, Fragment } from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import axios from 'axios'
import { bool, string, object, array, shape, arrayOf, number } from 'prop-types'

import useGenerationStatusWatcher from 'Pods/Shared/GenerationStatus/useGenerationStatusWatcher'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import UserSegmentationModal from 'Pods/Shared/UserSegmentationModal'
import UserSubscriptionsModal from 'Pods/Shared/UserSubscriptionsModal'
import SaleOffBar from 'Pods/TopNotificationBars/Components/SaleOffBar'
import SubscribeBar from 'Pods/TopNotificationBars/Components/SubscribeBar'

import { sendExperimentEvent } from 'Utils/Experiments'

import LegacyNavigation from './Components/LegacyNavigation/LegacyNavigation'
import Navigation from './Components/Navigation/Navigation'

const navId = 'navigation-header'

function UnifiedNavigation({
  extractingCoreStyles = false,
  unifiedNavData = {
    barForbidden: false,
    commonRoutes: {},
    mainMenuItems: {},
    locale: 'en',
    byType: [],
  },
  isInFastPage = false,
  isInListingPage = false,
  searchInputValue = '',
  newTopNavigation = false,
  // props from slow pages
  isSignedIn = false,
  lastTimeModalSeenCookieKey,
  displaySegmentationModal,
  subscriptionsModalClosedCookieKey,
  displaySubscriptionsModal,
  barForbidden,
  newRoyaltyUi: slowRoyaltyUi,
  showCartAds: slowShowCartAds = false,
  useAdvancedKeywordsSearch: slowUseAdvancedKeywordsSearch = false,
  // sale off bar props
  discountConfig = null,
  subscriptionTopbar: slowSubscriptionTopbar = false,
  isActiveSubscriber: slowIsActiveSubscriber = false,
  subscriptionMenuEnabled: slowSubscriptionMenuEnabled = false,
  subscriptionFlowEnabled: slowSubscriptionFlowEnabled = false,
}) {
  const {
    commonRoutes,
    mainMenuItems,
    legacyMainMenuItems,
    communityLinks,
    actionsLinks,
    locale,
    cldGiftSize,
    assetCategories,
    printableCategories,
    searchPlaceholder,
    byType,
    budgetsData,
  } = unifiedNavData

  const pageConfig = usePageConfig({ listener: 'full-page-config-loaded', checkIfAvailable: false })
  const [subscriptionTopbar, setSubscriptionTopbar] = useState(slowSubscriptionTopbar)
  const showSubscriptionBar = !!subscriptionTopbar && !barForbidden
  const showSaleOff = !showSubscriptionBar && !!discountConfig && !barForbidden
  const [actionsConfig, setActionsConfig] = useState({
    isCurrentUser: isSignedIn,
    cart: {},
    inboxSize: 0,
    notificationsSize: 0,
    newRoyaltyUi: slowRoyaltyUi,
    showCartAds: slowShowCartAds,
    subscriptionTopbar: slowSubscriptionTopbar,
    isActiveSubscriber: slowIsActiveSubscriber,
    subscriptionMenuEnabled: slowSubscriptionMenuEnabled,
    subscriptionFlowEnabled: slowSubscriptionFlowEnabled,
  })
  const [enableAdvancedKeywordsSearch, setEnableAdvancedKeywordsSearch] = useState(slowUseAdvancedKeywordsSearch)

  const isInSlowPage = !isInFastPage

  // Site-wide producer that keeps the global generation-status store fed off /ai too.
  useGenerationStatusWatcher({ isCurrentUser: actionsConfig.isCurrentUser })

  function setToken() {
    const metaToken = document.getElementsByName('csrf-token')[0]
    let token = null

    if (metaToken) {
      token = metaToken.getAttribute('content')
      axios.defaults.headers.common['X-CSRF-Token'] = token
    }

    axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'
  }

  useEffect(() => {
    if (isInSlowPage) {
      setActionsConfig({
        isCurrentUser: isSignedIn,
        cart: {},
        inboxSize: 0,
        notificationsSize: 0,
        newRoyaltyUi: slowRoyaltyUi,
        showCartAds: slowShowCartAds,
        subscriptionTopbar: slowSubscriptionTopbar,
        isActiveSubscriber: slowIsActiveSubscriber,
        subscriptionMenuEnabled: slowSubscriptionMenuEnabled,
        subscriptionFlowEnabled: slowSubscriptionFlowEnabled,
      })
    }
  }, [])

  useEffect(() => {
    if (!isInSlowPage && pageConfig) {
      readPageConfig()
    }
  }, [isInSlowPage, pageConfig])

  useEffect(() => {
    if (pageConfig) {
      sendExperimentEvent('disable_sitewide_discounts', pageConfig.disableSitewideDiscounts)
    }
  }, [pageConfig])

  useEffect(() => {
    const appHeader = document.getElementById('app-header')
    const notificationsContainer = document.getElementById('notifications-container')
    const hasTopBar = showSubscriptionBar || showSaleOff

    if (appHeader) {
      appHeader.classList.toggle('with-bar', extractingCoreStyles || hasTopBar)
    }

    if (notificationsContainer) {
      notificationsContainer.classList.toggle('with-bar', hasTopBar)
    }
  }, [extractingCoreStyles, showSubscriptionBar, showSaleOff])

  function readPageConfig() {
    setToken()

    const {
      cart,
      inboxSize,
      notificationsSize,
      isCurrentUser,
      newRoyaltyUi,
      showCartAds,
      useAdvancedKeywordsSearch,
      subscriptionTopbar: configSubscriptionTopbar,
      isActiveSubscriber: configIsActiveSubscriber,
      subscriptionMenuEnabled: configSubscriptionMenuEnabled,
      subscriptionFlowEnabled: configSubscriptionFlowEnabled,
    } = pageConfig

    window.dispatchEvent(
      new CustomEvent('navigation-cart-loaded', {
        detail: cart,
      })
    )

    setEnableAdvancedKeywordsSearch(useAdvancedKeywordsSearch)
    setSubscriptionTopbar(configSubscriptionTopbar)

    setActionsConfig({
      cart,
      inboxSize,
      notificationsSize,
      isCurrentUser,
      newRoyaltyUi,
      showCartAds,
      subscriptionTopbar: configSubscriptionTopbar,
      subscriptionMenuEnabled: configSubscriptionMenuEnabled,
      subscriptionFlowEnabled: configSubscriptionFlowEnabled,
      isActiveSubscriber: configIsActiveSubscriber,
    })
  }

  return (
    <nav
      id={navId}
      className={classnames('wayfinder', (extractingCoreStyles || showSubscriptionBar || showSaleOff) && 'with-bar')}
    >
      {showSubscriptionBar && (
        <SubscribeBar />
      )}

      {showSaleOff && (
        <SaleOffBar
          barColor={discountConfig.barColor}
          content={discountConfig.body}
          discountPhrase={discountConfig.discountPhrase}
          discount={discountConfig.noticeBarDiscount}
          endingDate={discountConfig.endingDate}
          useTimer={discountConfig.useTimer}
          locale={locale}
        />
      )}

      {newTopNavigation ? (
        <Navigation
          commonRoutes={commonRoutes}
          mainMenuItems={mainMenuItems}
          actionsLinks={actionsLinks}
          actionsConfig={actionsConfig}
          locale={locale}
          isInListingPage={isInListingPage}
          searchInputValue={searchInputValue}
          searchPlaceholder={searchPlaceholder}
        />
      ) : (
        <LegacyNavigation
          commonRoutes={commonRoutes}
          mainMenuItems={legacyMainMenuItems}
          communityLinks={communityLinks}
          actionsLinks={actionsLinks}
          actionsConfig={actionsConfig}
          locale={locale}
          cldGiftSize={cldGiftSize}
          isInListingPage={isInListingPage}
          searchInputValue={searchInputValue}
          printableCategories={printableCategories}
          assetCategories={assetCategories}
          enableAdvancedKeywordsSearch={enableAdvancedKeywordsSearch}
          extractingCoreStyles={extractingCoreStyles}
          searchPlaceholder={searchPlaceholder}
          byType={byType}
          budgetsData={budgetsData}
        />
      )}

      {isInSlowPage && (
        <Fragment>
          <UserSegmentationModal cookieKey={lastTimeModalSeenCookieKey} isModalOpen={displaySegmentationModal} />
          <UserSubscriptionsModal
            lastSeenCookieKey={lastTimeModalSeenCookieKey}
            modalClosedCookieKey={subscriptionsModalClosedCookieKey}
            isModalOpen={displaySubscriptionsModal}
          />
        </Fragment>
      )}
    </nav>
  )
}

UnifiedNavigation.propTypes = {
  extractingCoreStyles: bool,
  isInFastPage: bool,
  isInListingPage: bool,
  searchInputValue: string,
  newTopNavigation: bool,
  unifiedNavData: shape({
    barForbidden: bool,
    commonRoutes: object,
    mainMenuItems: object,
    legacyMainMenuItems: object,
    communityLinks: array,
    actionsLinks: arrayOf(shape({
      id: string,
      href: string,
      icon: string,
      data: shape({
        trackText: string,
        trackLabel: string,
        avoidNavigation: bool,
      }),
    })).isRequired,
    locale: string,
    assetCategories: object,
    printableCategories: object,
    budgetsData: shape({
      assetsBaseUrl: string.isRequired,
      printableBaseUrl: string.isRequired,
      assetsBudgets: arrayOf(shape({
        title: string.isRequired,
        path: string.isRequired,
        icon: string.isRequired,
        withSub: bool,
      })).isRequired,
      printableAssetsBudgets: arrayOf(shape({
        title: string.isRequired,
        path: string.isRequired,
        icon: string.isRequired,
        withSub: bool,
      })).isRequired,
    }).isRequired,
  }),
  isSignedIn: bool,
  showCartAds: bool,
  useAdvancedKeywordsSearch: bool,
  subscriptionTopbar: bool,
  isActiveSubscriber: bool,
  subscriptionMenuEnabled: bool,
  subscriptionFlowEnabled: bool,
  discountConfig: shape({
    barColor: string,
    body: string,
    discountPhrase: string,
    noticeBarDiscount: number,
    endingDate: number,
    useTimer: bool,
  }),
}

export default UnifiedNavigation
